import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {

    const [cart, setCart] = useState(() => {
        // 尝试从 sessionStorage 中获取购物车数据
        const sessionData = sessionStorage.getItem('cart');
        if (sessionData) {
            return JSON.parse(sessionData);
        } else {
            // 如果 sessionStorage 中没有数据，返回一个空数组
            sessionStorage.setItem('cart', JSON.stringify([]));
            return [];
        }
    });

    // 远端同步：标记当前的 setCart 是否来自远端，用于防止回环广播
    const isApplyingRemoteUpdate = useRef(false);
    // 本地变更的订阅者（由 useSharedCart 注册）
    const localChangeListenerRef = useRef(null);

    const setCartFromRemote = useCallback((newCart) => {
        isApplyingRemoteUpdate.current = true;
        setCart(Array.isArray(newCart) ? newCart : []);
        // 在下一个 tick 复位，useEffect 执行完毕后
        queueMicrotask(() => {
            isApplyingRemoteUpdate.current = false;
        });
    }, []);

    const subscribeLocalChange = useCallback((listener) => {
        localChangeListenerRef.current = listener;
        return () => {
            if (localChangeListenerRef.current === listener) {
                localChangeListenerRef.current = null;
            }
        };
    }, []);

    // 存放"最近一次本地变更"的描述，供订阅者拿到 op
    const pendingLocalOpRef = useRef(null);

    const swapDescriptionsInCart = () => {
        const swappedCart = cart.map(order => {
            // 复制订单以避免直接修改状态
            const newOrder = [...order];

            // 交换 mainItem 的描述
            [newOrder[1].mainItemDescription, newOrder[1].mainItemDescription1] =
            [newOrder[1].mainItemDescription1, newOrder[1].mainItemDescription];

            // 交换所有子项的描述
            for (let i = 2; i < newOrder.length; i++) {
                [newOrder[i].itemDescription, newOrder[i].itemDescription1] =
                [newOrder[i].itemDescription1, newOrder[i].itemDescription];
            }

            return newOrder;
        });

        setCart(swappedCart); // 更新购物车状态
    };





    const [totalQuantity, setTotalQuantity] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [totalGST, setTotalGST] = useState(0);

    // 当购物车变化时，更新 sessionStorage 和总量、总金额、总GST
    useEffect(() => {
        sessionStorage.setItem('cart', JSON.stringify(cart));
        setTotalQuantity(cart.reduce((total, order) => total + order[1].quantity, 0));
        setTotalAmount(getTotalAmount());
        setTotalGST(getTotalGST());

        // 仅本地变更时通知订阅者
        if (!isApplyingRemoteUpdate.current && localChangeListenerRef.current && pendingLocalOpRef.current) {
            const op = pendingLocalOpRef.current;
            pendingLocalOpRef.current = null;
            try {
                localChangeListenerRef.current({ cart, op });
            } catch (e) {
                console.error('[CartContext] local change listener error:', e);
            }
        } else {
            pendingLocalOpRef.current = null;
        }
    }, [cart]);

    const getMainItemQuantity = (mainItemCode) => {
        const mainItemOrder = cart.find(order => order.length === 2 && order[1].mainItemCode === mainItemCode);

        // 如果找到对应的订单，则返回 quantity，否则返回 0
        return mainItemOrder ? mainItemOrder[1].quantity : 0;
    };




    // 添加到购物车的逻辑
    const addOrderToCart = (orderItems) => {



        pendingLocalOpRef.current = { type: 'add', payload: orderItems };
        setCart(prevCart => {
            const newOrderId = prevCart.length + 1;
            const newMainItem = orderItems[0];
            //
            const newSubItems = orderItems.slice(1);

            let found = false;

            const newCart = prevCart.map(order => {
                // 检查是否存在相同的主项目和子项目
                if (order[1].mainItemCode === newMainItem.mainItemCode &&
                    order[1].multiplePricePosition === newMainItem.multiplePricePosition &&
                    JSON.stringify(order.slice(2)) === JSON.stringify(newSubItems)) {
                    found = true;
                    // 累加主项目的数量
                    const updatedOrder = [...order];
                    // console.log(updatedOrder[1].quantity, newMainItem.quantity);
                    updatedOrder[1] = { ...updatedOrder[1], quantity: updatedOrder[1].quantity + newMainItem.quantity };
                    // console.log(updatedOrder[1].quantity);
                    return updatedOrder;
                }
                return order;
            });


            // 如果没有找到相同的订单，添加新订单
            if (!found) {
                const newOrder = [newOrderId, ...orderItems];
                newCart.push(newOrder);
            }

            return newCart;
        });
    };

    const editaddOrderToCart = (orderToAdd) => {


        pendingLocalOpRef.current = { type: 'editAdd', payload: orderToAdd };
        setCart(prevCart => {
            const mainItemToAdd = orderToAdd[1];
            const subItemsToAdd= orderToAdd.slice(2);

            return prevCart.reduce((acc, order) => {
                if (order[1].mainItemCode === mainItemToAdd.mainItemCode &&
                    order[1].multiplePricePosition === mainItemToAdd.multiplePricePosition &&
                    JSON.stringify(order.slice(2)) === JSON.stringify(subItemsToAdd)) {

                    // 减少主项目的数量
                    const updatedOrder = [...order];
                    updatedOrder[1] = { ...updatedOrder[1], quantity: updatedOrder[1].quantity + 1 };

                    // 如果主项目的数量大于0，则保留更新后的订单
                    if (updatedOrder[1].quantity > 0) {
                        acc.push(updatedOrder);
                    }

                    // 如果主项目的数量为0，则不再添加到购物车中
                    return acc;
                } else {
                    // 如果订单不匹配，保留原订单
                    acc.push(order);
                    return acc;
                }
            }, []);
        });
    };

    const removeOrderFromCart = (orderToRemove) => {

        pendingLocalOpRef.current = { type: 'remove', payload: orderToRemove };
        setCart(prevCart => {
            const mainItemToRemove = orderToRemove[1];
            const subItemsToRemove = orderToRemove.slice(2);

            return prevCart.reduce((acc, order) => {
                if (order[1].mainItemCode === mainItemToRemove.mainItemCode &&
                    order[1].multiplePricePosition === mainItemToRemove.multiplePricePosition &&
                    JSON.stringify(order.slice(2)) === JSON.stringify(subItemsToRemove)) {

                    // 减少主项目的数量
                    const updatedOrder = [...order];
                    updatedOrder[1] = { ...updatedOrder[1], quantity: updatedOrder[1].quantity - 1 };

                    // 如果主项目的数量大于0，则保留更新后的订单
                    if (updatedOrder[1].quantity > 0) {
                        acc.push(updatedOrder);
                    }

                    // 如果主项目的数量为0，则不再添加到购物车中
                    return acc;
                } else {
                    // 如果订单不匹配，保留原订单
                    acc.push(order);
                    return acc;
                }
            }, []);
        });
    };


    const clearCart = () => {
        pendingLocalOpRef.current = { type: 'clear' };
        setCart([]);
    }


    // 计算购物车中所有 mainItem 的总数量
    const getTotalQuantity = () => {
        return cart.reduce((total, order) => {
            return total + order[1].quantity;
        }, 0);
    };
    function formatAmount(amount) {
        let formatted = amount.toFixed(2);
        formatted = parseFloat(formatted).toString();

        return formatted;
    }

    const getTotalAmount = () => {
        const totalAmount = cart.reduce((totalAmount, order) => {
            const mainItem = order[1];
            let orderAmount = mainItem.price * mainItem.quantity;

            // 如果有 submenu，计算它们的价格
            if (order.length > 2) {
                const subItems = order.slice(2);
                subItems.forEach(subItem => {
                    orderAmount += subItem.price * subItem.quantity * mainItem.quantity;
                });
            }

            return totalAmount + orderAmount;
        }, 0);
        return formatAmount(totalAmount);
    };

    const getTotalGST = () => {
        const totalGST = cart.reduce((totalGST, order) => {
            const mainItem = order[1];
            let orderGST = mainItem.price * mainItem.quantity * mainItem.taxrate / 100;

            // 如果有 submenu，计算它们的价格
            if (order.length > 2) {
                const subItems = order.slice(2);
                subItems.forEach(subItem => {
                    orderGST += subItem.price * subItem.quantity * mainItem.quantity * mainItem.taxrate / 100;
                });
            }

            return totalGST + orderGST;
        }, 0);
        return formatAmount(totalGST);
    };
    // const getOrderAmount = (order) => {
    //     const mainItem = order[0];
    //     let orderAmount = mainItem.price * mainItem.quantity;

    //     // 如果有 submenu，计算它们的价格
    //     const subItems = order.slice(1);
    //     subItems.forEach(subItem => {
    //         orderAmount += subItem.price * subItem.quantity * mainItem.quantity;
    //     });

    //     return formatAmount(orderAmount);
    // };

    // ... 其他操作，如删除、更新数量等

    return (
        <CartContext.Provider value={{ swapDescriptionsInCart,cart, addOrderToCart, totalQuantity , totalAmount, totalGST, removeOrderFromCart, editaddOrderToCart,getMainItemQuantity, clearCart, setCartFromRemote, subscribeLocalChange}}>
            {children}
        </CartContext.Provider>
    );
};
